/**
 * GLITCH Theme — Hero Slideshow
 *
 * Custom element handling:
 *  - Slide transitions (fade)
 *  - Autoplay with configurable speed
 *  - Prev/next arrows + dot navigation
 *  - Touch swipe on mobile
 *  - Pause on hover (desktop)
 *  - Pause when out of viewport (perf)
 *  - Keyboard navigation (arrow keys when focused)
 *  - Respects prefers-reduced-motion
 */

class HeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isHovered = false;
    this.isVisible = true;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slideshow__dot'));
    this.arrows = Array.from(this.querySelectorAll('.hero-slideshow__arrow'));
    this.track = this.querySelector('.hero-slideshow__track');

    if (this.slides.length <= 1) return;

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bindEvents();
    this.setupIntersectionObserver();

    if (this.autoplayEnabled && !this.reducedMotion) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
  }

  bindEvents() {
    // Arrow buttons
    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        if (arrow.dataset.direction === 'next') this.next();
        else this.prev();
        this.restartAutoplay();
      });
    });

    // Dot buttons
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.slideIndex, 10);
        this.goTo(index);
        this.restartAutoplay();
      });
    });

    // Pause on hover (desktop only — hover doesn't fire on touch)
    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
      this.stopAutoplay();
    });
    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
      if (this.autoplayEnabled && this.isVisible && !this.reducedMotion) {
        this.startAutoplay();
      }
    });

    // Touch swipe
    if (this.track) {
      this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
      this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });
    }

    // Keyboard navigation when slideshow is focused
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
        this.restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
        this.restartAutoplay();
      }
    });
  }

  setupIntersectionObserver() {
    // Pause autoplay when slideshow is not in viewport (performance)
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (this.autoplayEnabled && !this.isHovered && !this.reducedMotion) {
              this.startAutoplay();
            }
          } else {
            this.stopAutoplay();
          }
        });
      },
      { threshold: 0.25 }
    );
    this.observer.observe(this);
  }

  onTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
    this.touchStartY = e.changedTouches[0].screenY;
  }

  onTouchEnd(e) {
    const touchEndX = e.changedTouches[0].screenX;
    const touchEndY = e.changedTouches[0].screenY;
    const deltaX = touchEndX - this.touchStartX;
    const deltaY = touchEndY - this.touchStartY;

    // Only register as swipe if horizontal movement dominates (avoid scroll conflicts)
    if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > Math.abs(deltaX)) return;

    if (deltaX > 0) {
      this.prev();
    } else {
      this.next();
    }
    this.restartAutoplay();
  }

  goTo(index) {
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.currentIndex = index;
  }

  next() {
    this.goTo(this.currentIndex + 1);
  }

  prev() {
    this.goTo(this.currentIndex - 1);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    if (this.autoplayEnabled && !this.isHovered && this.isVisible && !this.reducedMotion) {
      this.startAutoplay();
    }
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
